import {
  Box,
  Flex,
  Grid,
  HStack,
  Stack,
  VStack,
  Text,
  Icon,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RiArrowLeftLine,
  RiFolder3Line,
  RiStackLine,
  RiTimeLine,
  RiAlertLine,
  RiCalendarLine,
  RiExternalLinkLine,
} from 'react-icons/ri';
import {
  FaLinkedin,
  FaTwitter,
  FaFacebook,
  FaInstagram,
  FaTiktok,
  FaYoutube,
  FaGlobe,
} from 'react-icons/fa';
import { MdOutlineSmartToy } from 'react-icons/md';
import {
  DigitalTwinViewDataFragment,
  useMarkTwisViewedMutation,
  useGetBehaviorChangesCountForDigitalTwinQuery,
} from '../../generated/graphql';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';
import { HelivanLabel } from '../helivan/HelivanLabel';
import { ProjectCard } from '../Projects/ProjectCard';
import { capitalize } from '../../utils/capitalize';

interface DigitalTwinViewProps {
  digitalTwin: DigitalTwinViewDataFragment;
}

interface StatCardProps {
  icon: any;
  label: string;
  value: string | number;
  sublabel?: string;
  highlight?: boolean;
}

const StatCard = ({ icon, label, value, sublabel, highlight }: StatCardProps) => (
  <Flex
    bg="white"
    borderRadius="10px"
    border="1px solid"
    borderColor={highlight ? HelivanColor.text.helivanBlue : HelivanColor.border.primary}
    p="14px"
    alignItems="center"
    gap={3}
    flex="1"
    minW="140px"
  >
    <Flex
      w="36px"
      h="36px"
      borderRadius="8px"
      bg={highlight ? 'rgba(0,71,125,0.12)' : 'rgba(0,71,125,0.08)'}
      align="center"
      justify="center"
      flexShrink={0}
    >
      <Icon as={icon} boxSize="18px" color={HelivanColor.text.helivanBlue} />
    </Flex>
    <Box>
      <Text fontSize="10px" fontWeight={500} color={HelivanColor.text.blueMid} textTransform="uppercase" letterSpacing="0.5px">
        {label}
      </Text>
      <Text fontSize="20px" fontWeight={700} color={HelivanColor.text.blueDark} lineHeight="1.2">
        {value}
      </Text>
      {sublabel && (
        <Text fontSize="10px" color={HelivanColor.text.blueMid}>
          {sublabel}
        </Text>
      )}
    </Box>
  </Flex>
);

interface SocialLinkProps {
  icon: any;
  url: string;
  label: string;
}

const SocialLink = ({ icon, url, label }: SocialLinkProps) => (
  <Tooltip label={label} fontSize="xs">
    <IconButton
      aria-label={label}
      icon={<Icon as={icon} boxSize="18px" />}
      size="sm"
      variant="ghost"
      color={HelivanColor.text.blueDark}
      _hover={{ bg: HelivanColor.background.secondary, color: HelivanColor.text.helivanBlue }}
      onClick={() => window.open(url, '_blank')}
    />
  </Tooltip>
);

const formatDate = (date: string | Date) => {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatRelativeTime = (date: string | Date) => {
  const now = new Date();
  const then = new Date(date);
  const diffInDays = Math.floor((now.getTime() - then.getTime()) / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return 'Yesterday';
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  return formatDate(date);
};

export const DigitalTwinView = ({ digitalTwin }: DigitalTwinViewProps) => {
  const [markViewed] = useMarkTwisViewedMutation();
  const navigate = useNavigate();

  // Fetch actual behavior changes count for this digital twin
  const { data: changesData } = useGetBehaviorChangesCountForDigitalTwinQuery({
    variables: { digitalTwinId: digitalTwin.id },
  });

  useEffect(() => {
    markViewed({
      variables: {
        id: digitalTwin.id,
      },
    });
  }, [digitalTwin.id, markViewed]);

  // Calculate statistics
  const stats = useMemo(() => {
    const projects = digitalTwin.projects ?? [];

    // Get unique project templates (by topic)
    const uniqueTopics = Array.from(new Set(projects.map(p => p.topicShortened ?? p.topic)));

    // Sort projects by date to find most recent
    const sortedProjects = [...projects].sort((a, b) =>
      new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()
    );

    const mostRecentProject = sortedProjects[0];

    // Use actual changes count from backend
    const changesDetected = changesData?.getBehaviorChangesCountForDigitalTwin ?? 0;

    return {
      projectTemplateCount: uniqueTopics.length,
      totalExplorables: projects.length,
      changesDetected,
      mostRecentDate: mostRecentProject?.createdAt,
      mostRecentTopic: mostRecentProject?.topicShortened ?? mostRecentProject?.topic,
    };
  }, [digitalTwin.projects, changesData]);

  // Get role title based on digital twin type
  const getRoleTitle = () => {
    if (digitalTwin.congressData) {
      return `${capitalize(digitalTwin.congressData.chamber)} - ${digitalTwin.congressData.state}`;
    }
    if (digitalTwin.earlyStageVcData) {
      return digitalTwin.earlyStageVcData.title
        ? `${digitalTwin.earlyStageVcData.title} at ${digitalTwin.earlyStageVcData.firm}`
        : digitalTwin.earlyStageVcData.firm;
    }
    if (digitalTwin.fortuneCeoData) {
      return `CEO, ${digitalTwin.fortuneCeoData.company}`;
    }
    if (digitalTwin.fedMemberData) {
      const fed = digitalTwin.fedMemberData;
      return fed.office === 'president'
        ? fed.title
        : `${fed.title}, Federal Reserve Board`;
    }
    return null;
  };

  // Get social links based on digital twin type
  const getSocialLinks = () => {
    const links: SocialLinkProps[] = [];

    if (digitalTwin.congressData) {
      if (digitalTwin.congressData.linkedinHandle) {
        links.push({ icon: FaLinkedin, url: `https://linkedin.com/in/${digitalTwin.congressData.linkedinHandle}`, label: 'LinkedIn' });
      }
      if (digitalTwin.congressData.twitterHandle) {
        links.push({ icon: FaTwitter, url: `https://x.com/${digitalTwin.congressData.twitterHandle}`, label: 'X (Twitter)' });
      }
    }

    if (digitalTwin.sendrilData) {
      if (digitalTwin.sendrilData.twitterHandle) {
        links.push({ icon: FaTwitter, url: `https://x.com/${digitalTwin.sendrilData.twitterHandle}`, label: 'X (Twitter)' });
      }
      if (digitalTwin.sendrilData.facebookHandle) {
        links.push({ icon: FaFacebook, url: `https://facebook.com/${digitalTwin.sendrilData.facebookHandle}`, label: 'Facebook' });
      }
      if (digitalTwin.sendrilData.instagramHandle) {
        links.push({ icon: FaInstagram, url: `https://instagram.com/${digitalTwin.sendrilData.instagramHandle}`, label: 'Instagram' });
      }
      if (digitalTwin.sendrilData.tiktokHandle) {
        links.push({ icon: FaTiktok, url: `https://tiktok.com/@${digitalTwin.sendrilData.tiktokHandle}`, label: 'TikTok' });
      }
      if (digitalTwin.sendrilData.youtubeHandle) {
        links.push({ icon: FaYoutube, url: `https://youtube.com/@${digitalTwin.sendrilData.youtubeHandle}`, label: 'YouTube' });
      }
      if (digitalTwin.sendrilData.website) {
        links.push({ icon: FaGlobe, url: digitalTwin.sendrilData.website, label: 'Website' });
      }
    }

    if (digitalTwin.earlyStageVcData) {
      if (digitalTwin.earlyStageVcData.linkedinHandle) {
        links.push({ icon: FaLinkedin, url: `https://linkedin.com/in/${digitalTwin.earlyStageVcData.linkedinHandle}`, label: 'LinkedIn' });
      }
      if (digitalTwin.earlyStageVcData.twitterHandle) {
        links.push({ icon: FaTwitter, url: `https://x.com/${digitalTwin.earlyStageVcData.twitterHandle}`, label: 'X (Twitter)' });
      }
    }

    if (digitalTwin.fortuneCeoData) {
      if (digitalTwin.fortuneCeoData.linkedinHandle) {
        links.push({ icon: FaLinkedin, url: `https://linkedin.com/in/${digitalTwin.fortuneCeoData.linkedinHandle}`, label: 'LinkedIn' });
      }
      if (digitalTwin.fortuneCeoData.twitterHandle) {
        links.push({ icon: FaTwitter, url: `https://x.com/${digitalTwin.fortuneCeoData.twitterHandle}`, label: 'X (Twitter)' });
      }
    }

    if (digitalTwin.fedMemberData?.bioSource) {
      links.push({ icon: FaGlobe, url: digitalTwin.fedMemberData.bioSource, label: 'Official Fed profile' });
    }

    return links;
  };

  const roleTitle = getRoleTitle();
  const socialLinks = getSocialLinks();

  // Get unique projects for display (deduplicated by topic)
  const uniqueProjects = useMemo(() => {
    const seen = new Set<string>();
    return (digitalTwin.projects ?? []).filter(project => {
      const topic = project.topicShortened ?? project.topic;
      if (seen.has(topic)) return false;
      seen.add(topic);
      return true;
    });
  }, [digitalTwin.projects]);

  return (
    <Stack width="full" height="full" spacing={0} overflow="hidden">
      {/* Header */}
      <Flex
        width="full"
        alignItems="center"
        py="12px"
        px="24px"
        bg={HelivanColor.background.primary}
        borderBottom="1px solid"
        borderBottomColor={HelivanColor.border.primary}
        gap={4}
        flexShrink={0}
      >
        <IconButton
          aria-label="Go back"
          icon={<RiArrowLeftLine size={18} />}
          size="sm"
          variant="ghost"
          color={HelivanColor.text.blueMid}
          onClick={() => navigate('/personas')}
        />
        <Box flex="1">
          <HStack spacing={3}>
            <Flex
              w="40px"
              h="40px"
              borderRadius="10px"
              bg="rgba(0,71,125,0.08)"
              align="center"
              justify="center"
            >
              <MdOutlineSmartToy size={20} color={HelivanColor.text.helivanBlue} />
            </Flex>
            <Box>
              <HStack spacing={2}>
                <HelivanText variant="sectionHeader">{digitalTwin.name}</HelivanText>
                <HelivanLabel label="Persona" variant="blue" size="sm" />
              </HStack>
              {roleTitle && (
                <Text fontSize="13px" color={HelivanColor.text.blueMid}>
                  {roleTitle}
                </Text>
              )}
            </Box>
          </HStack>
        </Box>
        {socialLinks.length > 0 && (
          <HStack spacing={1}>
            {socialLinks.map((link, idx) => (
              <SocialLink key={idx} {...link} />
            ))}
          </HStack>
        )}
      </Flex>

      {/* Content */}
      <Flex
        flex="1"
        overflowY="auto"
        bg={HelivanColor.background.secondary}
        p={6}
      >
        <VStack spacing={6} width="full" maxWidth="1200px" mx="auto" align="stretch">
          {/* Stats Section */}
          <Box>
            <Text fontSize="14px" fontWeight={600} color={HelivanColor.text.blueDark} mb={3}>
              Agent Statistics
            </Text>
            <Flex gap={3} flexWrap="wrap">
              <StatCard
                icon={RiFolder3Line}
                label="Projects"
                value={stats.projectTemplateCount}
                sublabel="unique topics"
              />
              <StatCard
                icon={RiStackLine}
                label="Watches"
                value={stats.totalExplorables}
                sublabel="total"
              />
              <StatCard
                icon={RiAlertLine}
                label="Changes"
                value={stats.changesDetected}
                sublabel="detected"
                highlight={stats.changesDetected > 0}
              />
              <StatCard
                icon={RiCalendarLine}
                label="Last Activity"
                value={stats.mostRecentDate ? formatRelativeTime(stats.mostRecentDate) : 'N/A'}
                sublabel={stats.mostRecentTopic ? `on "${stats.mostRecentTopic}"` : undefined}
              />
            </Flex>
          </Box>

          {/* Blurb/Description if available */}
          {digitalTwin.blurb && (
            <Box
              bg="white"
              borderRadius="10px"
              border="1px solid"
              borderColor={HelivanColor.border.primary}
              p="16px"
            >
              <Text fontSize="13px" color={HelivanColor.text.blueDark} lineHeight="1.6">
                {digitalTwin.blurb}
              </Text>
            </Box>
          )}

          {/* Additional Metadata for specific types */}
          {(digitalTwin.congressData || digitalTwin.earlyStageVcData || digitalTwin.fortuneCeoData || digitalTwin.fedMemberData) && (
            <Box
              bg="white"
              borderRadius="10px"
              border="1px solid"
              borderColor={HelivanColor.border.primary}
              p="16px"
            >
              <Text fontSize="12px" fontWeight={600} color={HelivanColor.text.blueMid} mb={2} textTransform="uppercase" letterSpacing="0.5px">
                Details
              </Text>
              <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
                {digitalTwin.congressData && (
                  <>
                    <Box>
                      <Text fontSize="11px" color={HelivanColor.text.blueMid}>Party</Text>
                      <Text fontSize="14px" fontWeight={500} color={HelivanColor.text.blueDark}>
                        {capitalize(digitalTwin.congressData.party)}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="11px" color={HelivanColor.text.blueMid}>Chamber</Text>
                      <Text fontSize="14px" fontWeight={500} color={HelivanColor.text.blueDark}>
                        {capitalize(digitalTwin.congressData.chamber)}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="11px" color={HelivanColor.text.blueMid}>State</Text>
                      <Text fontSize="14px" fontWeight={500} color={HelivanColor.text.blueDark}>
                        {digitalTwin.congressData.state.toUpperCase()}
                      </Text>
                    </Box>
                  </>
                )}
                {digitalTwin.earlyStageVcData && (
                  <>
                    <Box>
                      <Text fontSize="11px" color={HelivanColor.text.blueMid}>Firm</Text>
                      <Text fontSize="14px" fontWeight={500} color={HelivanColor.text.blueDark}>
                        {digitalTwin.earlyStageVcData.firm}
                      </Text>
                    </Box>
                    {digitalTwin.earlyStageVcData.title && (
                      <Box>
                        <Text fontSize="11px" color={HelivanColor.text.blueMid}>Position</Text>
                        <Text fontSize="14px" fontWeight={500} color={HelivanColor.text.blueDark}>
                          {digitalTwin.earlyStageVcData.title}
                        </Text>
                      </Box>
                    )}
                    {digitalTwin.earlyStageVcData.location && (
                      <Box>
                        <Text fontSize="11px" color={HelivanColor.text.blueMid}>Location</Text>
                        <Text fontSize="14px" fontWeight={500} color={HelivanColor.text.blueDark}>
                          {digitalTwin.earlyStageVcData.location}
                        </Text>
                      </Box>
                    )}
                  </>
                )}
                {digitalTwin.fortuneCeoData && (
                  <>
                    <Box>
                      <Text fontSize="11px" color={HelivanColor.text.blueMid}>Company</Text>
                      <Text fontSize="14px" fontWeight={500} color={HelivanColor.text.blueDark}>
                        {digitalTwin.fortuneCeoData.company}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="11px" color={HelivanColor.text.blueMid}>Industry</Text>
                      <Text fontSize="14px" fontWeight={500} color={HelivanColor.text.blueDark}>
                        {capitalize(digitalTwin.fortuneCeoData.industry)}
                      </Text>
                    </Box>
                  </>
                )}
                {digitalTwin.fedMemberData && (
                  <>
                    <Box>
                      <Text fontSize="11px" color={HelivanColor.text.blueMid}>Role</Text>
                      <Text fontSize="14px" fontWeight={500} color={HelivanColor.text.blueDark}>
                        {digitalTwin.fedMemberData.title}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="11px" color={HelivanColor.text.blueMid}>Institution</Text>
                      <Text fontSize="14px" fontWeight={500} color={HelivanColor.text.blueDark}>
                        {digitalTwin.fedMemberData.bank}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="11px" color={HelivanColor.text.blueMid}>FOMC Vote</Text>
                      <Text fontSize="14px" fontWeight={500} color={HelivanColor.text.blueDark}>
                        {digitalTwin.fedMemberData.isVoting ? 'Voting member' : 'Non-voting (rotation)'}
                      </Text>
                    </Box>
                  </>
                )}
              </Grid>
            </Box>
          )}

          {/* Projects Section */}
          <Box>
            <HStack spacing={2} mb={3}>
              <Text fontSize="14px" fontWeight={600} color={HelivanColor.text.blueDark}>
                Projects
              </Text>
              <Text fontSize="12px" color={HelivanColor.text.blueMid}>
                ({uniqueProjects.length} unique topics)
              </Text>
            </HStack>
            {uniqueProjects.length > 0 ? (
              <Grid
                templateColumns={{
                  base: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                  lg: 'repeat(4, 1fr)',
                }}
                gap={4}
              >
                {uniqueProjects.map((project) => (
                  <ProjectCard
                    onClick={() => navigate(`/projects/${project.id}`)}
                    dateCreated={project.createdAt ?? ''}
                    id={project.id}
                    industry={project.digitalTwinType}
                    subtopics={project.subtopicsShortened ?? []}
                    title={project.topicShortened ?? project.topic}
                    key={project.id}
                  />
                ))}
              </Grid>
            ) : (
              <Flex
                bg="white"
                borderRadius="10px"
                border="1px solid"
                borderColor={HelivanColor.border.primary}
                p="40px"
                justifyContent="center"
                alignItems="center"
              >
                <Text fontSize="14px" color={HelivanColor.text.blueMid}>
                  No projects yet for this agent
                </Text>
              </Flex>
            )}
          </Box>
        </VStack>
      </Flex>
    </Stack>
  );
};
